import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
    CreateTaskFormValues,
    createTaskFormSchema,
} from '@/features/end-of-lease/ProjectDetailView/CreateTaskDialog';
import {
    EditTaskFormValues,
    editTaskFormSchema,
} from '@/features/end-of-lease/ProjectDetailView/EditTaskDialog';
import { Header } from '@/features/end-of-lease/ProjectDetailView/Header';
import { IATABinder } from '@/features/end-of-lease/ProjectDetailView/IATABinder';
import { LeftSidebar } from '@/features/end-of-lease/ProjectDetailView/LeftSidebar';
import { Summary } from '@/features/end-of-lease/ProjectDetailView/Summary';
import ProjectTimeline from '@/features/end-of-lease/ProjectTimeline';
import {
    fetchAllPaginatedItems,
    listComponents,
    useComplianceProjectSummary,
    useCreateProjectTask,
    useProject,
    useProjectDocComplianceFailures,
    useUpdateProjectTask,
} from '@/features/end-of-lease/api';
import {
    ComponentWithInstalledItem,
    ProjectTask,
    ProjectTaskParty,
    ProjectTaskPriority,
    ProjectTaskStatus,
} from '@/features/end-of-lease/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute } from '@tanstack/react-router';
import { format } from 'date-fns';
import { toast } from 'sonner';

import {
    PROJECT_TASK_PARTIES,
    PROJECT_TASK_PRIORITIES,
    PROJECT_TASK_STATUSES,
} from '@/lib/constants';
import { LIST_VIEW_RETURN_KEY } from '@/lib/listViewState';

import { useAuthAxios } from '@/hooks/useAuthAxios';

import { LoadingScreen } from '@/components/loading-screen';

import { ComplianceFailureAlert } from '@/features/AssetDetailView/ProjectDetailView/ComplianceFailureAlert';

export const Route = createFileRoute('/lease/projects/$projectId/page')({
    component: LeasingProjectDetailPage,
});

function LeasingProjectDetailPage() {
    const navigate = Route.useNavigate();
    const { projectId: _projectId } = Route.useParams();
    const projectId = Number(_projectId);

    const { client, enabled } = useAuthAxios();
    const [allComponents, setAllComponents] = useState<ComponentWithInstalledItem[]>([]);
    const [isCreateTaskDialogOpen, setIsCreateTaskDialogOpen] =
        useState<boolean>(false);
    const [isEditTaskDialogOpen, setIsEditTaskDialogOpen] =
        useState<boolean>(false);
    const [selectedTask, setSelectedTask] = useState<ProjectTask | null>(null);

    const createProjectTask = useCreateProjectTask(projectId);
    const updateProjectTask = useUpdateProjectTask(projectId);

    const createTaskForm = useForm<CreateTaskFormValues>({
        resolver: zodResolver(createTaskFormSchema),
        defaultValues: {
            name: '',
            party: '',
            assigneeId: '',
            priority: '',
            startDate: undefined,
            endDate: undefined,
        },
        mode: 'onChange',
    });

    const editTaskForm = useForm<EditTaskFormValues>({
        resolver: zodResolver(editTaskFormSchema),
        defaultValues: {
            status: undefined,
            assigneeId: '',
            priority: undefined,
            startDate: undefined,
            endDate: undefined,
        },
        mode: 'onChange',
    });

    const handleCreateDialogOpenChange = (open: boolean) => {
        if (!open) createTaskForm.reset();

        setIsCreateTaskDialogOpen(open);
    };

    const handleCreateTaskSubmit = (data: CreateTaskFormValues) => {
        createProjectTask.mutate(
            {
                name: data.name,
                startDate: format(data.startDate, 'yyyy-MM-dd'),
                endDate: format(data.endDate, 'yyyy-MM-dd'),
                status: ProjectTaskStatus.NotStarted,
                priority: data.priority as ProjectTaskPriority,
                assigneeId: Number(data.assigneeId),
                party: data.party as ProjectTaskParty,
            },
            {
                onSuccess: () => {
                    toast.success('Task created successfully!');
                    createTaskForm.reset();
                    setIsCreateTaskDialogOpen(false);
                },
                onError: (error: any) => {
                    const message =
                        error.response?.data?.detail ||
                        error.message ||
                        'Failed to create task.';
                    toast.error(message);
                },
            },
        );
    };

    const handleEditDialogOpenChange = (open: boolean) => {
        if (!open) {
            editTaskForm.reset();
            setSelectedTask(null);
        }
        setIsEditTaskDialogOpen(open);
    };

    const handleTaskClick = (task: ProjectTask) => {
        setSelectedTask(task);
        editTaskForm.reset({
            status: task.status,
            assigneeId: task.assigneeId?.toString() || '',
            priority: task.priority,
            startDate: new Date(task.startDate),
            endDate: new Date(task.endDate),
        });
        setIsEditTaskDialogOpen(true);
    };

    const handleEditTaskSubmit = (formData: EditTaskFormValues) => {
        if (!formData.assigneeId || !selectedTask) return;

        updateProjectTask.mutate(
            {
                taskId: selectedTask.id,
                data: {
                    status: formData.status,
                    priority: formData.priority,
                    assigneeId: Number(formData.assigneeId),
                    startDate: format(formData.startDate, 'yyyy-MM-dd'),
                    endDate: format(formData.endDate, 'yyyy-MM-dd'),
                },
            },
            {
                onSuccess: () => {
                    toast.success('Task updated successfully!');
                    setIsEditTaskDialogOpen(false);
                    setSelectedTask(null);
                },
                onError: (error: any) => {
                    const message =
                        error.response?.data?.detail ||
                        error.message ||
                        'Failed to update task.';
                    toast.error(message);
                },
            },
        );
    };

    const { data: complianceSummary } = useComplianceProjectSummary(projectId);
    const { data: complianceFailures = [] } = useProjectDocComplianceFailures(projectId);

    // TODO: Refactor the queries below to have useAuthAxios inside them by default
    useEffect(() => {
        const fetchAllComponents = async () => {
            const allFetchedItems = await fetchAllPaginatedItems<ComponentWithInstalledItem>(
                ({ limit, skip }) =>
                    listComponents(client, projectId, limit, skip),
            );
            setAllComponents(allFetchedItems);
        };

        fetchAllComponents();
    }, [projectId]);

    const { data: project, isLoading: isProjectLoading } =
        useProject(projectId);

    const personOptions = project
        ? project.members.map((m) => ({
              value: m.userId.toString(),
              label: m.name,
          }))
        : [];

    // Asks the dashboard to restore its filters, unlike a sidebar visit.
    const handleBack = () => {
        // Cast because `/lease`'s validator doesn't know `_lvReturn`.
        navigate({
            to: '/lease',
            search: { [LIST_VIEW_RETURN_KEY]: true },
        } as unknown as Parameters<typeof navigate>[0]);
    };

    const navigateToFolder = (letter: string) => {
        navigate({
            to: '/lease/projects/$projectId/folders/$folderLetter',
            params: {
                projectId: projectId.toString(),
                folderLetter: letter,
            },
        });
    };

    if (!project || isProjectLoading) {
        return <LoadingScreen />;
    }

    return (
        <div
            className="flex flex-col bg-white min-h-screen"
            data-testid="NewLeaseProjectDetail"
        >
            <Header
                projectId={project.id}
                title={project.title}
                onBack={handleBack}
            />
            <Summary project={project} components={allComponents} />
            <div className="flex flex-1 gap-0">
                <LeftSidebar />
                <div className="flex-1 p-6 space-y-6">
                    <ComplianceFailureAlert failures={complianceFailures} />
                    <ProjectTimeline
                        tasks={project.tasks ?? []}
                        parties={PROJECT_TASK_PARTIES}
                        persons={personOptions}
                        priorities={PROJECT_TASK_PRIORITIES}
                        statusOptions={PROJECT_TASK_STATUSES}
                        isCreateTaskDialogOpen={isCreateTaskDialogOpen}
                        selectedTask={selectedTask}
                        isEditTaskDialogOpen={isEditTaskDialogOpen}
                        toggleEditDialogOpen={() =>
                            handleEditDialogOpenChange(!isEditTaskDialogOpen)
                        }
                        onTaskClick={handleTaskClick}
                        createTaskForm={createTaskForm}
                        onCreateDialogOpenChange={handleCreateDialogOpenChange}
                        onCreateTaskSubmit={handleCreateTaskSubmit}
                        editTaskForm={editTaskForm}
                        onEditTaskSubmit={handleEditTaskSubmit}
                    />
                    <IATABinder
                        complianceSummary={complianceSummary}
                        navigateToFolder={navigateToFolder}
                    />
                </div>
            </div>
        </div>
    );
}

export default LeasingProjectDetailPage;
